import { Badge, Box, Card, Group, SegmentedControl, Skeleton, Stack, Text } from '@mantine/core';
import { Link, useSearchParams } from 'react-router';
import type { ClientQueryRowDto } from '@mexp/shared';
import { ErrorState } from '@/components/States';
import { useClientQueries } from './api';
import { fullDate } from './stage';

const FILTERS = ['all', 'open', 'answered'] as const;
type Filter = (typeof FILTERS)[number];

/** One query: the dossier it is on, what the client asked, and whether Makin has sent its compliance. */
function QueryRow({ q }: { q: ClientQueryRowDto }) {
  return (
    <Box component={Link} to={`/requests/${q.requestId}`} className="portal-row">
      <Group justify="space-between" align="flex-start" wrap="wrap" gap="sm">
        <Stack gap={4} style={{ minWidth: 0, flex: '1 1 320px' }}>
          <Text fw={600} lh={1.3}>
            {q.productName}{' '}
            <Text span c="dimmed" fw={400} inherit>
              for {q.country}
            </Text>
          </Text>
          <Text fz="xs" c="dimmed" className="tabular">
            {q.code} · Query {q.number} · raised {fullDate(q.requestedAt.slice(0, 10))}
          </Text>
          <Text fz="sm" lineClamp={2}>
            {q.comment}
          </Text>
        </Stack>
        <Stack gap={2} align="flex-end" style={{ flex: 'none' }}>
          <Badge color={q.open ? 'grape' : 'teal'} variant="light" size="md">
            {q.open ? 'Pending' : 'Compliance sent'}
          </Badge>
          {q.answeredAt && (
            <Text fz="xs" c="dimmed" className="tabular">
              {fullDate(q.answeredAt.slice(0, 10))}
            </Text>
          )}
        </Stack>
      </Group>
    </Box>
  );
}

/**
 * Every query the company raised on its dossiers, newest first: all of them, the ones waiting for Makin's compliance,
 * or the ones answered. The filter lives in the URL (`status`) so the dashboard can link straight to it.
 */
export function ClientQueries() {
  const queries = useClientQueries();
  const [params, setParams] = useSearchParams();
  const filter: Filter = (FILTERS as readonly string[]).includes(params.get('status') ?? '') ? (params.get('status') as Filter) : 'all';
  const setFilter = (next: string) => {
    const out = new URLSearchParams(params);
    if (next === 'all') out.delete('status');
    else out.set('status', next);
    setParams(out, { replace: true });
  };

  if (queries.isError) return <ErrorState error={queries.error} onRetry={() => void queries.refetch()} />;
  const all = queries.data ?? [];
  const open = all.filter((q) => q.open);
  const answered = all.filter((q) => !q.open);
  const shown = filter === 'open' ? open : filter === 'answered' ? answered : all;

  return (
    <Stack gap="md">
      <SegmentedControl
        w="fit-content"
        value={filter}
        onChange={setFilter}
        data={[
          { value: 'all', label: `All · ${all.length}` },
          { value: 'open', label: `Pending · ${open.length}` },
          { value: 'answered', label: `Compliance sent · ${answered.length}` },
        ]}
      />
      <Card p={0}>
        <Box p={6}>
          {queries.isLoading ? (
            <Stack gap="xs" p="sm">
              <Skeleton h={64} />
              <Skeleton h={64} />
            </Stack>
          ) : shown.length ? (
            <Stack gap={2}>
              {shown.map((q) => (
                <QueryRow key={`${q.requestId}-${q.number}`} q={q} />
              ))}
            </Stack>
          ) : (
            <Text fz="sm" c="dimmed" p="md">
              {filter === 'open'
                ? 'No query is waiting for Makin.'
                : filter === 'answered'
                  ? 'Makin has not answered a query yet.'
                  : 'No queries yet. Raise one from a released dossier’s page when the authority asks for changes.'}
            </Text>
          )}
        </Box>
      </Card>
    </Stack>
  );
}
